/* verqo — Portal Journey Preview
   Combines Option 2 (real portal dashboard) + Option 4 (scroll storytelling).
   Uses REAL screenshots from the live seller + buyer portals.
*/
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import {
  ArrowLeft, ChevronRight, FileText, Brain, Banknote, Truck, Repeat,
  ShoppingCart, Wallet, Search, TrendingUp, ShieldCheck, ScrollText, Building2,
  Sparkles,
} from "lucide-react";
import { VerqoLockup } from "../../components/VerqoLockup";

const BRAND = { fg: "#0c1612", accent: "#dcff3a" };

function MacBrowserFrame({ url, children, testid, accent = true, fill = false }) {
  return (
    <div className={`rounded-xl border bg-white overflow-hidden shadow-[0_40px_80px_-32px_rgba(15,23,42,0.35)] ${accent ? "border-lime-400" : "border-slate-200"} ${fill ? "h-full flex flex-col" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/70 flex-shrink-0">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-300" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-300" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
        </div>
        <div className="ml-2 px-2.5 py-0.5 rounded-md bg-white border border-slate-200 text-[10.5px] text-slate-500 tracking-wide truncate flex-1 max-w-[320px]">
          {url}
        </div>
        <div className="text-[10px] text-emerald-600 inline-flex items-center gap-1 font-semibold uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
        </div>
      </div>
      <div className={`relative bg-white ${fill ? "flex-1 flex flex-col" : ""}`}>{children}</div>
    </div>
  );
}

/* Hub                                                                    */
const JOURNEYS = [
  {
    slug: "seller",
    name: "Seller journey",
    icon: Wallet,
    color: "bg-lime-300 text-slate-900",
    blurb: "Walk a wholesale seller through every step — from a PO landing in their inbox to same-day cash in their wallet, then auto-debit at day 60.",
    scenes: 4,
  },
  {
    slug: "buyer",
    name: "Buyer journey",
    icon: Building2,
    color: "bg-slate-900 text-white",
    blurb: "Walk a wholesale buyer through every step — from one approval that unlocks every supplier on verqo, to ordering, to paying back at Net-60.",
    scenes: 5,
  },
];

export function PortalJourneyHub() {
  useEffect(() => { document.title = "verqo · Portal journey previews"; }, []);
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" data-testid="journey-hub">
      <header className="border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/preview/landing-v2" className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900 transition-colors" data-testid="back-to-landing">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to landing
            </Link>
            <span className="w-px h-4 bg-slate-200" />
            <VerqoLockup />
          </div>
          <span className="text-[11px] text-slate-400">portal journey · real screens</span>
        </div>
      </header>

      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-12">
        <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 mb-3 inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> Option 2 + Option 4 · live combo
        </div>
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight max-w-3xl leading-tight">
          Watch the real portal <span className="text-slate-400">come alive as you scroll.</span>
        </h1>
        <p className="mt-5 text-[15px] text-slate-600 max-w-2xl leading-relaxed">
          Sticky portal screen on the right · narrative on the left. As you scroll, the screen updates to show the next step.
          Every screen is a <strong className="text-slate-900">real screenshot from the live verqo portal</strong> — no mockups.
        </p>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="journey-grid">
          {JOURNEYS.map((j) => (
            <Link
              key={j.slug}
              to={`/preview/portal-journey/${j.slug}`}
              className="group relative rounded-2xl border border-slate-200 bg-white hover:border-lime-400 hover:-translate-y-1 hover:shadow-[0_24px_64px_-24px_rgba(132,204,22,0.25)] transition-all p-7"
              data-testid={`journey-${j.slug}`}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="w-11 h-11 rounded-lg grid place-items-center" style={{ background: BRAND.fg }}>
                  <j.icon className="w-4 h-4" style={{ color: BRAND.accent }} strokeWidth={1.8} />
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold ${j.color}`}>
                  {j.scenes} scenes
                </span>
              </div>
              <div className="text-xl font-semibold tracking-tight text-slate-900 leading-snug">{j.name}</div>
              <div className="mt-3 text-[13.5px] leading-relaxed text-slate-600">{j.blurb}</div>
              <div className="mt-7 inline-flex items-center gap-1.5 text-[12px] font-semibold text-lime-700 group-hover:gap-2.5 transition-all">
                Open journey <ChevronRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

/* Shared scroll journey component                                         */
export function InlinePortalTabs({ scenes, autoRotateMs = 5500, testid = "inline-portal-tabs" }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((a) => (a + 1) % scenes.length), autoRotateMs);
    return () => clearInterval(t);
  }, [paused, scenes.length, autoRotateMs]);

  const a = scenes[active];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.55fr] gap-8 lg:gap-10 items-start" data-testid={testid}>
      {/* Left — vertical tab list · compact so 4 cards align to the right 16:10 frame */}
      <div className="flex flex-col gap-2" onMouseEnter={() => setPaused(true)}>
        {scenes.map((s, i) => {
          const isActive = i === active;
          return (
            <button
              key={s.num}
              onClick={() => { setActive(i); setPaused(true); }}
              className={`group w-full text-left p-3.5 rounded-xl border transition-all relative overflow-hidden ${
                isActive
                  ? "border-lime-400 bg-white shadow-[0_18px_48px_-28px_rgba(132,204,22,0.35)]"
                  : "border-slate-200 bg-white/40 hover:bg-white hover:border-slate-300"
              }`}
              data-testid={`portal-tab-${i}`}
              aria-pressed={isActive}
            >
              {/* Top auto-rotate progress bar (active + not paused only) */}
              {isActive && !paused && (
                <motion.div
                  key={`progress-${i}-${active}`}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: autoRotateMs / 1000, ease: "linear" }}
                  className="absolute top-0 left-0 right-0 h-0.5 bg-lime-500 origin-left"
                />
              )}
              <div className="flex items-start gap-3">
                <div
                  className={`w-8 h-8 rounded-lg grid place-items-center flex-shrink-0 transition-colors ${isActive ? "" : "bg-slate-100 group-hover:bg-slate-200/70"}`}
                  style={isActive ? { background: BRAND.fg } : undefined}
                >
                  <s.icon
                    className={`w-3.5 h-3.5 ${isActive ? "" : "text-slate-500"}`}
                    style={isActive ? { color: BRAND.accent } : undefined}
                    strokeWidth={1.8}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[9.5px] uppercase tracking-[0.22em] font-bold ${isActive ? "text-lime-700" : "text-slate-400"}`}>
                      {s.num} · {s.tag}
                    </span>
                  </div>
                  <div className={`text-[13.5px] font-semibold tracking-tight leading-snug transition-colors ${isActive ? "text-slate-900" : "text-slate-700"}`}>
                    {s.title}
                  </div>
                  <div className={`mt-1 text-[11.5px] leading-snug line-clamp-2 ${isActive ? "text-slate-600" : "text-slate-500"}`}>
                    {s.tabBlurb || s.body}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Right — sticky screenshot panel with fixed 16:10 aspect ratio (matches captures) */}
      <div className="relative lg:sticky lg:top-24">
        <MacBrowserFrame url={a.url} testid="tabs-frame">
          <div className="relative w-full bg-slate-50 overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
            <AnimatePresence mode="wait">
              <motion.img
                key={a.image}
                src={a.image}
                alt={a.title}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 w-full h-full object-contain object-top bg-slate-50"
                data-testid={`tabs-image-${a.num}`}
              />
            </AnimatePresence>
            {/* Annotation chip */}
            <AnimatePresence>
              <motion.div
                key={`anno-${a.num}`}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="absolute top-4 left-4 max-w-[280px] px-3 py-2 rounded-lg shadow-lg backdrop-blur-md flex items-start gap-2"
                style={{ background: "rgba(12, 22, 18, 0.92)" }}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-lime-300 animate-pulse mt-1.5 flex-shrink-0" />
                <div>
                  <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold" style={{ color: BRAND.accent }}>{a.tag}</div>
                  <div className="text-[12px] text-white font-medium leading-snug mt-0.5">{a.annotation}</div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </MacBrowserFrame>
        {/* Floating ETA badge */}
        <motion.div
          key={`eta-${a.num}`}
          initial={{ opacity: 0, scale: 0.9, x: 12 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ duration: 0.45 }}
          className="absolute -right-3 top-12 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold shadow-lg hidden md:block"
          style={{ background: BRAND.fg, color: BRAND.accent }}
        >
          {a.eta}
        </motion.div>
      </div>
    </div>
  );
}

export function InlinePortalJourney({ scenes, accent = "bg-lime-300 text-slate-900", testid = "inline-portal-journey" }) {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    return scrollYProgress.on("change", (v) => {
      const idx = Math.min(scenes.length - 1, Math.max(0, Math.floor(v * scenes.length)));
      setActiveIdx(idx);
    });
  }, [scrollYProgress, scenes.length]);

  const active = scenes[activeIdx];

  return (
    <div ref={sectionRef} className="relative" style={{ height: `${scenes.length * 90}vh` }} data-testid={testid}>
      <div className="sticky top-24 grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-10 lg:gap-14 items-center">
        {/* Left — narrative */}
        <div className="relative py-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.num}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="text-[64px] md:text-[80px] leading-none font-semibold tracking-tighter text-lime-300">
                {active.num}
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-lime-700 font-semibold">
                <active.icon className="w-3.5 h-3.5" /> {active.tag}
              </div>
              <div className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight leading-tight">{active.title}</div>
              <p className="mt-5 text-[15px] leading-relaxed text-slate-600 max-w-md">{active.body}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {active.chips.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] uppercase tracking-wider bg-lime-50 text-lime-700 border border-lime-200 font-semibold">
                    {c}
                  </span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Progress dots */}
          <div className="mt-10 space-y-2">
            {scenes.map((s, i) => (
              <div key={s.num} className="flex items-center gap-3">
                <div className={`h-1 rounded-full transition-all ${i === activeIdx ? "w-10 bg-slate-900" : "w-4 bg-slate-200"}`} />
                <span className={`text-[11px] tabular-nums uppercase tracking-wider ${i === activeIdx ? "text-slate-900 font-semibold" : "text-slate-400"}`}>
                  {s.num} · {s.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right — sticky real portal screenshot */}
        <div className="relative">
          <MacBrowserFrame url={active.url} testid="journey-frame">
            <div className="relative aspect-[14/9] overflow-hidden bg-slate-50">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active.image}
                  src={active.image}
                  alt={active.title}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.45 }}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                  data-testid={`screen-image-${active.num}`}
                />
              </AnimatePresence>
              {/* Top-left annotation chip */}
              <AnimatePresence>
                <motion.div
                  key={`anno-${active.num}`}
                  initial={{ opacity: 0, x: -20, y: -8 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                  className="absolute top-4 left-4 max-w-[280px] px-3 py-2 rounded-lg shadow-lg backdrop-blur-md flex items-start gap-2"
                  style={{ background: "rgba(12, 22, 18, 0.92)" }}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-lime-300 animate-pulse mt-1.5 flex-shrink-0" />
                  <div>
                    <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold" style={{ color: BRAND.accent }}>{active.tag}</div>
                    <div className="text-[12px] text-white font-medium leading-snug mt-0.5">{active.annotation}</div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </MacBrowserFrame>
          {/* Floating ETA badge */}
          <motion.div
            key={`eta-${active.num}`}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className={`absolute -right-3 top-12 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold shadow-lg hidden md:block ${accent}`}
          >
            {active.eta}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function JourneyView({ role, title, sub, accent, scenes }) {
  useEffect(() => { document.title = `verqo · ${title}`; }, [title]);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/preview/portal-journey" className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900 transition-colors" data-testid="back-to-journey-hub">
              <ArrowLeft className="w-3.5 h-3.5" /> Both journeys
            </Link>
            <span className="w-px h-4 bg-slate-200" />
            <VerqoLockup />
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold ${accent}`}>
              {role} journey
            </span>
            <span className="text-[11px] text-slate-400 hidden md:inline">scroll to play</span>
          </div>
        </div>
      </header>

      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-12 md:pt-14 pb-8">
        <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 mb-3">Live in the portal · real screens</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl">{title}</h1>
        <p className="mt-3 text-[14px] text-slate-600 max-w-2xl">{sub}</p>
      </section>

      <main className="max-w-[1280px] mx-auto px-6 md:px-10">
        <InlinePortalJourney scenes={scenes} accent={accent} testid="scroll-journey" />

        <div className="text-center text-[12px] text-slate-500 py-12">
          ↓ scroll to play the full journey · {scenes.length} scenes ↓
        </div>

        {/* Final CTA at bottom */}
        <div className="max-w-3xl mx-auto rounded-2xl text-white p-8 md:p-10 mb-24" style={{ background: BRAND.fg }} data-testid="journey-cta">
          <div className="text-[10.5px] uppercase tracking-[0.28em] mb-2" style={{ color: BRAND.accent }}>That's the whole flow</div>
          <div className="text-2xl md:text-3xl font-semibold tracking-tight leading-tight">
            From PO to paid in <span style={{ color: BRAND.accent }}>under a day</span>.<br />
            Across {scenes.length} live portal screens.
          </div>
          <div className="mt-6">
            <Link
              to="/preview/landing-v2"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
              style={{ background: BRAND.accent, color: BRAND.fg }}
              data-testid="journey-final-cta"
            >
              See the full landing page <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* SELLER JOURNEY                                                         */
/* ────────────────────────────────────────────────────────────────────── */
export const SELLER_SCENES = [
  {
    num: "01", tag: "Orders", icon: ScrollText,
    title: "Every buyer PO parsed in seconds.",
    tabBlurb: "PDF · email · CSV · Shopify · API. No more manual re-keying or chasing missing line items.",
    body: "Buyer drops a Net-60 order in your verqo Orders inbox — whether they used the catalog, a PDF PO, or sent it via API. verqo parses every line item in seconds. No data entry, no QuickBooks reconciliation.",
    chips: ["PDF · CSV · API", "Auto-parsed", "3 awaiting"],
    eta: "T+0 sec",
    image: "/preview-screens/seller/orders.png?v=3",
    url: "seller.verqo.com / orders",
    annotation: "9 orders this week · 3 awaiting confirmation · 3 in progress.",
  },
  {
    num: "02", tag: "Buyer Intelligence", icon: TrendingUp,
    title: "See who you're shipping to — live.",
    tabBlurb: "Live Verqo Trade Score · cash flow · sales velocity · payment history. Decide terms with eyes open.",
    body: "Click any buyer to open the 360° drawer — Verqo Trade Score, total/paid/outstanding volume, KYB verification, active terms, and full order + invoice history. Updates with every PO so you decide terms with full visibility, not guesswork.",
    chips: ["Verqo Trade Score", "360° buyer drawer", "Updates per PO"],
    eta: "T+30 sec",
    image: "/preview-screens/seller/buyer-drawer.png?v=3",
    url: "seller.verqo.com / buyers",
    annotation: "Verqo Demo Retail · Score 747 (Very Good) · KYB verified · Net-30 · $50K active.",
  },
  {
    num: "03", tag: "Credit", icon: Brain,
    title: "Pre-approved buyers, multi-funder pool.",
    tabBlurb: "AI underwrites every invoice. Factor, insure, or extend lines — fee scales by term.",
    body: "Your seller credit line is auto-issued at signup. Pick the term (Net-14 to Net-120), the lender carries the receivable, Allianz Trade insures the buyer side. 92% advance on every approved invoice — fund the ones you choose, one click per invoice.",
    chips: ["$100K line", "Net-14 → Net-120", "92% advance"],
    eta: "T+1 min",
    image: "/preview-screens/seller/credit.png?v=3",
    url: "seller.verqo.com / wallet / credit",
    annotation: "Available $86,383 of $100K · Net-30 = 1.00% ($100/10k) · Allianz-insured.",
  },
  {
    num: "04", tag: "Payments", icon: Banknote,
    title: "Same-day cash, ready to use instantly.",
    tabBlurb: "Funded invoices land in your verqo wallet within minutes. Spend on inventory, payroll, or withdraw to bank — no waiting on Net-60.",
    body: "verqo wires the invoice advance (92%) into your verqo wallet on day 0 — same business day, often within minutes. The cash is live: pay suppliers, restock inventory, or withdraw to your bank anytime. Your buyer still pays Net-60; you don't wait.",
    chips: ["Same-day ACH", "Live wallet balance", "Withdraw any time"],
    eta: "T+1 hour",
    image: "/preview-screens/seller/wallet.png?v=3",
    url: "seller.verqo.com / wallet",
    annotation: "Live wallet · ledger in sync · same-day ACH · spend or withdraw instantly.",
  },
];

export function PortalJourneySeller() {
  return (
    <JourneyView
      role="seller"
      title="The seller journey · from PO to paid in one day."
      sub="Four scenes. Every screen is a live screenshot of the actual verqo seller portal. Scroll to watch the cash arrive."
      accent="bg-lime-300 text-slate-900"
      scenes={SELLER_SCENES}
    />
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* BUYER JOURNEY                                                          */
/* ────────────────────────────────────────────────────────────────────── */
const BUYER_SCENES = [
  {
    num: "01", tag: "One credit line · every supplier", icon: TrendingUp,
    title: "Approved in seconds. Re-usable everywhere on verqo.",
    body: "Your verqo Score updates live as you trade. One credit line — Net-30, Net-60, Net-90, even Net-120 — works at every supplier on verqo. No more paper trade-references at each new vendor.",
    chips: ["Sub-60s approval", "Portable across suppliers", "Re-usable line"],
    eta: "T+0 sec",
    image: "/preview-screens/buyer/score.png",
    url: "buyer.verqo.com / score",
    annotation: "Live verqo Score · 725 / 1000 · top 0% of buyers · auto-recalibrated daily.",
  },
  {
    num: "02", tag: "Browse + order", icon: ShoppingCart,
    title: "Wholesale catalog. One-click Net-60.",
    body: "Browse every supplier on verqo from one catalog. Add to cart, pick your terms at checkout, and the order goes straight to the supplier's portal — pre-approved, pre-funded.",
    chips: ["Unified catalog", "1-click Net-60", "No re-apply"],
    eta: "T+30 sec",
    image: "/preview-screens/buyer/catalog.png",
    url: "buyer.verqo.com / catalog",
    annotation: "Pre-approved suppliers · pick terms inline · ship in days, pay in 60.",
  },
  {
    num: "03", tag: "Goods ship", icon: Truck,
    title: "Order accepted. Goods en route.",
    body: "Supplier accepts the PO and ships. You track every order in one place. Tracking, exceptions, and delivery confirmations flow back into the same ledger that holds your invoice.",
    chips: ["Live tracking", "Carrier-integrated", "Exception alerts"],
    eta: "T+ 1-7 days",
    image: "/preview-screens/buyer/orders.png",
    url: "buyer.verqo.com / orders",
    annotation: "Order accepted · supplier shipping · tracking live in your dashboard.",
  },
  {
    num: "04", tag: "Working capital unlocked", icon: Wallet,
    title: "Your capital page shows what you've got, and what's free.",
    body: "Open lines, available balance, terms breakdown by supplier. Re-balance your inventory buys against your live credit headroom — never order blind, never miss a re-stock window.",
    chips: ["Live headroom", "Multi-supplier", "Net-30 / 60 / 90 / 120"],
    eta: "T+ Day 1-60",
    image: "/preview-screens/buyer/capital.png",
    url: "buyer.verqo.com / capital",
    annotation: "Working-capital snapshot · live headroom across every supplier line.",
  },
  {
    num: "05", tag: "Repay · auto", icon: Repeat,
    title: "Net-60 auto-debits. Done.",
    body: "Day-60 hits. verqo pulls the agreed amount from your wallet automatically — no manual transfers, no missed payments, no late-fee surprises. Your score gets stronger. Your limits grow with you.",
    chips: ["Auto-debit", "Stronger score", "Higher limits"],
    eta: "Day 60",
    image: "/preview-screens/buyer/payments.png",
    url: "buyer.verqo.com / payments",
    annotation: "Auto-debit settled · score +3 · credit limit recalibrated.",
  },
];

export function PortalJourneyBuyer() {
  return (
    <JourneyView
      role="buyer"
      title="The buyer journey · one credit line, every supplier."
      sub="Five scenes. Every screen is a live screenshot of the actual verqo buyer portal. Scroll to watch your wholesale running capital come to life."
      accent="bg-slate-900 text-white"
      scenes={BUYER_SCENES}
    />
  );
}
